import type { IntlShape, MessageDescriptor } from 'react-intl';
import type { CampaignResultsPage } from '../outreachTypes';
import { angleText } from '../review/reviewLabels';
import { pitchKindGroupName } from '../live/pitchKindCopy';
import { resultsMessages as messages } from './resultsMessages';

export type ResultGroup = 'kind' | 'size' | 'angle';
export type LearnedSentenceKey = NonNullable<CampaignResultsPage['learned']['sentenceKey']>;

const SIZES: Record<string, MessageDescriptor> = {
  small: messages.sizeSmall,
  medium: messages.sizeMedium,
  large: messages.sizeLarge,
};

const SOURCES: Record<string, MessageDescriptor> = {
  spotifyForArtists: messages.sourceSpotifyForArtists,
  ditto: messages.sourceDitto,
  deezerForCreators: messages.sourceDeezerForCreators,
  youtubeAnalytics: messages.sourceYoutubeAnalytics,
};

const ADD_SOURCES: Record<string, MessageDescriptor> = {
  detected: messages.sourceDetected,
  reply: messages.sourceReply,
  manual: messages.sourceManual,
};

/** The fixed "learned" sentence of each key (design outreach-v2 n1): never AI text. */
export const LEARNED_SENTENCES: Record<LearnedSentenceKey, MessageDescriptor> = {
  smallPlaylists: messages.learnedSmallPlaylists,
  blogs: messages.learnedBlogs,
  fewerCreators: messages.learnedFewerCreators,
};

/** The label of one group row of "What worked". An unknown key shows as it is. */
export function resultGroupLabel(intl: IntlShape, group: ResultGroup, key: string): string {
  if (group === 'kind') {
    return pitchKindGroupName(intl, key);
  }
  if (group === 'size') {
    const size = SIZES[key];
    return size === undefined ? key : intl.formatMessage(size);
  }
  return key === 'other' ? intl.formatMessage(messages.angleOther) : angleText(intl, key);
}

/** "Spotify for Artists": the source of the streams. */
export function streamsSourceName(intl: IntlShape, source: string | null): string {
  const message = source === null ? undefined : SOURCES[source];
  return message === undefined ? (source ?? '') : intl.formatMessage(message);
}

/** "found by Plug", "from a reply", "marked by you". */
export function addSourceText(intl: IntlShape, source: string): string {
  const message = ADD_SOURCES[source];
  return message === undefined ? source : intl.formatMessage(message);
}

/** The rate of a part as a percent with one decimal under 10 ("4.2"), else whole. */
export function ratePercent(part: number, total: number): number {
  if (total <= 0) {
    return 0;
  }
  const value = (part / total) * 100;
  return value < 10 ? Math.round(value * 10) / 10 : Math.round(value);
}
